import { axiosInstance } from '../../../common/axiosInstans';
import { InventoryReminderScheduleConfig } from '../../../types/inventoryReminder';

export interface FeaturesAndConfigurations {
    features: Record<string, { enabled: boolean }>;
    configurations: {
        'force-double-signature'?: {
            withdrawal: boolean;
            deposit: boolean;
            inventory: boolean;
            disposal: boolean;
        };
        'inventory-reminder-schedule'?: InventoryReminderScheduleConfig | null;
        [key: string]: unknown;
    };
}

class SettingsService {
    /**
     * Get settings for the current organization
     */
    async getSettings(): Promise<FeaturesAndConfigurations> {
        const response = await axiosInstance.get('/settings/web');
        return response.data;
    }

    /**
     * Get the full features and configurations for an organization
     */
    async getFeaturesAndConfigurations(
        organizationId: string
    ): Promise<FeaturesAndConfigurations> {
        const response = await axiosInstance.get(
            `/organizations/${organizationId}/features-and-configurations`
        );
        return response.data;
    }

    /**
     * Update the full features and configurations for an organization
     */
    async updateFeaturesAndConfigurations(
        organizationId: string,
        featuresAndConfigurations: FeaturesAndConfigurations
    ): Promise<void> {
        await axiosInstance.put(
            `/organizations/${organizationId}/features-and-configurations`,
            {
                featuresAndConfigurations: JSON.stringify(
                    featuresAndConfigurations
                ),
            }
        );
    }

    /**
     * Update the inventory reminder schedule configuration
     */
    async updateInventoryReminderSchedule(
        organizationId: string,
        config: InventoryReminderScheduleConfig | null
    ): Promise<void> {
        // Get current configuration
        const currentConfig =
            await this.getFeaturesAndConfigurations(organizationId);

        // Update only the inventory-reminder-schedule
        const updatedConfig: FeaturesAndConfigurations = {
            ...currentConfig,
            configurations: {
                ...currentConfig.configurations,
                'inventory-reminder-schedule': config,
            },
        };

        // Save the updated configuration
        await this.updateFeaturesAndConfigurations(
            organizationId,
            updatedConfig
        );
    }

    /**
     * Get the inventory reminder schedule configuration
     */
    async getInventoryReminderSchedule(): Promise<InventoryReminderScheduleConfig | null> {
        try {
            const settings = await this.getSettings();
            return (
                settings.configurations['inventory-reminder-schedule'] || null
            );
        } catch (error) {
            console.error('Error fetching inventory reminder schedule:', error);
            return null;
        }
    }
}

export default SettingsService;
